/**
 * #2934 A11Y-09 (report ref T-02) - WCAG 1.3.1 Info and Relationships.
 *
 * Bestseller chart ranks exist only as CSS generated content:
 *
 *   .category-landing.chart-numbered            { counter-reset: chart-rank; }
 *   .category-landing.chart-numbered .chart-page-products .product-tile,
 *   .chart-numbered .nfp04                      { counter-increment: chart-rank; }
 *   ...::before                                 { content: counter(chart-rank); }
 *
 * CSS `content` is inconsistently exposed to assistive technology, so the entire proposition
 * of a "Top 20" - that it is ranked, and which book is number one - was carried by
 * presentation alone.
 *
 * An <ol> cannot hold the sequence: interstitial product-interruption blocks are inserted
 * between entries, and the counter is shared across two element types rendered by different
 * systems (.product-tile by the React app, .nfp04 by CMS blocks). Neither renderer can see
 * the other's contribution, so no server-side template knows an entry's rank.
 *
 * The DOM is the only place the full interleaved order exists. This walks the same selectors
 * the CSS counter uses, so the spoken rank cannot drift from the printed one.
 */

/** Mirrors the counter-reset scope. */
const ROOT = '.chart-numbered';

/** Mirrors the counter-increment selectors, in the same order the CSS applies them. */
const RANKED = '.chart-page-products .product-tile, .nfp04';

const RANK_CLASS = 'chart-rank-label';

const rankLabel = (rank: number) => `Number ${rank}. `;

/** Returned when there is no chart on the page, so callers can always call the result. */
const noop = () => undefined;

const applyToRoot = (root: Element) => {
  const entries = root.querySelectorAll(RANKED);

  entries.forEach((entry, index) => {
    const rank = index + 1;
    const existing = entry.querySelector(`:scope > .${RANK_CLASS}`);

    if (existing) {
      // Re-rank in place rather than replacing: interstitials and "Show More" both shift
      // the sequence without the entries themselves changing.
      if (existing.textContent !== rankLabel(rank)) {
        existing.textContent = rankLabel(rank);
      }

      return;
    }

    const label = document.createElement('span');
    label.className = `visually-hidden ${RANK_CLASS}`;
    label.textContent = rankLabel(rank);
    entry.prepend(label);
  });
};

/**
 * Outermost roots only - a nested `.chart-numbered` would otherwise restart the sequence
 * partway down the page, which is not what the shared CSS counter does.
 */
const chartRoots = () =>
  [...document.querySelectorAll(ROOT)].filter(
    root => root.parentElement?.closest(ROOT) == null,
  );

export const applyChartRanks = () => {
  const roots = chartRoots();

  if (roots.length === 0) {
    return noop;
  }

  for (const root of roots) {
    applyToRoot(root);
  }

  // The React list renders after mount and grows on "Show More", and editors can add
  // interruption blocks anywhere in the stream, so the sequence has to be recomputed rather
  // than assigned once.
  const observers = roots.map(root => {
    const watch = () =>
      observer.observe(root, { childList: true, subtree: true });

    // applyToRoot writes into the subtree being watched. It converges on its own - a second
    // pass finds every label already correct and mutates nothing - but disconnecting while
    // writing makes that a property of the code rather than of the timing.
    const observer = new MutationObserver(() => {
      observer.disconnect();
      applyToRoot(root);
      watch();
    });

    watch();

    return observer;
  });

  return () => {
    for (const observer of observers) {
      observer.disconnect();
    }
  };
};
